import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Loader } from '../components/ui/Loader';
import { TransactionCard } from '../components/balance/TransactionCard';
import { TransactionFilters } from '../components/balance/TransactionFilters';
import { useGetMyTransactionsQuery } from '../store/api/balanceApi';
import type { TransactionFilters as TransactionFiltersType } from '../store/api/balanceApi';

export function TransactionsPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<TransactionFiltersType>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, error } = useGetMyTransactionsQuery(filters);

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <h2 className="text-2xl font-heading font-bold text-danger mb-4">
                {t('transactions.error.title')}
              </h2>
              <p className="text-neutral-medium font-body mb-6">
                {'data' in (error as { data?: { message?: string } })
                  ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
                  : t('errors.network')}
              </p>
              <Link
                to="/balance"
                className="inline-block px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
              >
                {t('transactions.error.back')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const transactions = data?.data.transactions || [];
  const pagination = data?.data.pagination;

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center animate-slide-up">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
              {t('transactions.title')}
            </h1>
            <p className="text-neutral-medium text-lg font-body">
              {t('transactions.subtitle')}
            </p>
          </div>

          {/* Filters */}
          <div className="mb-6 animate-scale-in">
            <TransactionFilters filters={filters} onFiltersChange={setFilters} />
          </div>

          {/* Transactions List */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 mb-6 animate-scale-in">
            {transactions.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-neutral-medium text-lg font-body mb-4">
                  {t('transactions.empty')}
                </p>
                <Link
                  to="/recharge"
                  className="inline-block px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
                >
                  {t('transactions.emptyAction')}
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {transactions.map((transaction) => (
                  <TransactionCard key={transaction._id} transaction={transaction} />
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mb-6 animate-scale-in">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('transactions.pagination.previous')}
              </button>
              
              <div className="px-4 py-2 bg-neutral-dark/50 text-neutral-light font-body rounded-lg">
                {t('transactions.pagination.page')} {pagination.page} {t('transactions.pagination.of')} {pagination.pages}
              </div>
              
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('transactions.pagination.next')}
              </button>
            </div>
          )}

          {/* Pagination Info */}
          {pagination && (
            <div className="text-center text-neutral-medium font-body mb-6">
              {t('transactions.pagination.showing')} {transactions.length} {t('transactions.pagination.of')} {pagination.total} {t('transactions.pagination.transactions')}
            </div>
          )}

          {/* Back Button */}
          <div className="text-center">
            <Link
              to="/balance"
              className="inline-block px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300"
            >
              {t('transactions.backToBalance')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

